import { useNavigate } from 'react-router-dom';
import PageNavbar from '../../../components/feature/PageNavbar';

export default function MyroPage() {
  const navigate = useNavigate();

  const features = [
    {
      icon: 'ri-tablet-line',
      title: 'Large Interactive Touchscreen',
      description: 'Spacious touch-sensitive surface that responds to hand and arm movements for engaging therapy exercises'
    },
    {
      icon: 'ri-gamepad-line',
      title: 'Diverse Exercise Library',
      description: 'Extensive collection of cognitive and motor training games designed by rehabilitation specialists'
    },
    {
      icon: 'ri-brain-line',
      title: 'Cognitive Training',
      description: 'Exercises that challenge memory, attention, problem-solving, and executive function skills'
    },
    {
      icon: 'ri-hand-coin-line',
      title: 'Fine Motor Skills',
      description: 'Activities targeting hand-eye coordination, precision movements, and dexterity improvement'
    },
    {
      icon: 'ri-line-chart-line',
      title: 'Performance Tracking',
      description: 'Automatic recording of patient performance with detailed analytics and progress reports'
    },
    {
      icon: 'ri-wheelchair-line',
      title: 'Mobile Design',
      description: 'Wheeled frame allows easy positioning and use in various therapy settings and patient rooms'
    }
  ];

  const trainingModes = [
    {
      title: 'Cognitive Training',
      icon: 'ri-brain-line',
      activities: [
        'Memory matching games',
        'Pattern recognition exercises',
        'Attention and concentration tasks',
        'Problem-solving challenges'
      ]
    },
    {
      title: 'Motor Training',
      icon: 'ri-hand-heart-line',
      activities: [
        'Reaching and touching exercises',
        'Dragging and sliding movements',
        'Precision tapping activities',
        'Bilateral coordination tasks'
      ]
    },
    {
      title: 'Visual Perception',
      icon: 'ri-eye-line',
      activities: [
        'Visual scanning exercises',
        'Spatial awareness training',
        'Object tracking activities',
        'Visual discrimination tasks'
      ]
    },
    {
      title: 'Reaction Training',
      icon: 'ri-flashlight-line',
      activities: [
        'Speed response exercises',
        'Quick decision-making tasks',
        'Timing and rhythm activities',
        'Multi-stimulus challenges'
      ]
    }
  ];

  const suitableFor = [
    {
      condition: 'Stroke Rehabilitation',
      description: 'Helps restore cognitive function and upper limb motor skills after stroke',
      icon: 'ri-heart-pulse-line'
    },
    {
      condition: 'Traumatic Brain Injury',
      description: 'Supports cognitive recovery and motor relearning following brain trauma',
      icon: 'ri-mental-health-line'
    },
    {
      condition: 'Parkinson\'s Disease',
      description: 'Maintains cognitive abilities and fine motor control in Parkinson\'s patients',
      icon: 'ri-user-heart-line'
    },
    {
      condition: 'Multiple Sclerosis',
      description: 'Addresses cognitive challenges and motor coordination difficulties in MS',
      icon: 'ri-shield-cross-line'
    },
    {
      condition: 'Pediatric Rehabilitation',
      description: 'Engaging therapy tool for children with developmental or neurological conditions',
      icon: 'ri-parent-line'
    },
    {
      condition: 'Geriatric Care',
      description: 'Helps maintain cognitive function and motor skills in elderly patients',
      icon: 'ri-user-smile-line'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <PageNavbar />

      {/* Hero Section */}
      <section className="py-12 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Cognitive & Motor Rehabilitation Combined</h2>
              <p className="text-gray-600 text-base leading-relaxed mb-6">
                Myro is an innovative interactive rehabilitation table featuring a large touchscreen surface that 
                combines cognitive training with motor skill development. This versatile device offers a wide range 
                of engaging exercises that simultaneously challenge the mind and body, making therapy sessions more 
                effective and enjoyable.
              </p>
              <p className="text-gray-600 text-base leading-relaxed">
                With its mobile design and extensive exercise library, Myro can be easily positioned for individual 
                or group therapy sessions, providing flexible rehabilitation solutions for patients with various 
                neurological and orthopedic conditions.
              </p>
            </div>
            <div className="rounded-2xl overflow-hidden bg-gray-50">
              <img
                src="https://public.readdy.ai/ai/img_res/d8df5d9aba7d486c4943de1907b57b92.jpg"
                alt="Myro Interactive Table"
                className="w-full h-96 object-cover object-top"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Key Features - Natural Layout */}
      <section className="py-12 px-6 border-t border-gray-100">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">Key Features</h2>
          
          <div className="space-y-6">
            <div className="border-l-4 border-gray-900 pl-6">
              <h3 className="text-lg font-bold text-gray-900 mb-2">Large Interactive Touchscreen</h3>
              <p className="text-gray-600 text-base leading-relaxed">
                Spacious touch-sensitive surface that responds to hand and arm movements for engaging therapy exercises
              </p>
            </div>

            <div className="border-l-4 border-gray-900 pl-6">
              <h3 className="text-lg font-bold text-gray-900 mb-2">Diverse Exercise Library</h3>
              <p className="text-gray-600 text-base leading-relaxed">
                Extensive collection of cognitive and motor training games designed by rehabilitation specialists
              </p>
            </div>

            <div className="border-l-4 border-gray-900 pl-6">
              <h3 className="text-lg font-bold text-gray-900 mb-2">Cognitive Training</h3>
              <p className="text-gray-600 text-base leading-relaxed">
                Exercises that challenge memory, attention, problem-solving, and executive function skills
              </p>
            </div>

            <div className="border-l-4 border-gray-900 pl-6">
              <h3 className="text-lg font-bold text-gray-900 mb-2">Fine Motor Skills</h3>
              <p className="text-gray-600 text-base leading-relaxed">
                Activities targeting hand-eye coordination, precision movements, and dexterity improvement
              </p>
            </div>

            <div className="border-l-4 border-gray-900 pl-6">
              <h3 className="text-lg font-bold text-gray-900 mb-2">Performance Tracking</h3>
              <p className="text-gray-600 text-base leading-relaxed">
                Automatic recording of patient performance with detailed analytics and progress reports
              </p>
            </div>

            <div className="border-l-4 border-gray-900 pl-6">
              <h3 className="text-lg font-bold text-gray-900 mb-2">Mobile Design</h3>
              <p className="text-gray-600 text-base leading-relaxed">
                Wheeled frame allows easy positioning and use in various therapy settings and patient rooms
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Training Modes - Two Column Layout */}
      <section className="py-12 px-6 border-t border-gray-100">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">Training Modes</h2>
          
          <div className="grid md:grid-cols-2 gap-x-16 gap-y-8">
            <div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">Cognitive Training</h3>
              <ul className="space-y-2 text-gray-600 text-base">
                <li>• Memory matching games</li>
                <li>• Pattern recognition exercises</li>
                <li>• Attention and concentration tasks</li>
                <li>• Problem-solving challenges</li>
              </ul>
            </div>

            <div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">Motor Training</h3>
              <ul className="space-y-2 text-gray-600 text-base">
                <li>• Reaching and touching exercises</li>
                <li>• Dragging and sliding movements</li>
                <li>• Precision tapping activities</li>
                <li>• Bilateral coordination tasks</li>
              </ul>
            </div>

            <div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">Visual Perception</h3>
              <ul className="space-y-2 text-gray-600 text-base">
                <li>• Visual scanning exercises</li>
                <li>• Spatial awareness training</li>
                <li>• Object tracking activities</li>
                <li>• Visual discrimination tasks</li>
              </ul>
            </div>

            <div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">Reaction Training</h3>
              <ul className="space-y-2 text-gray-600 text-base">
                <li>• Speed response exercises</li>
                <li>• Quick decision-making tasks</li>
                <li>• Timing and rhythm activities</li>
                <li>• Multi-stimulus challenges</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Suitable For - Simple List */}
      <section className="py-12 px-6 border-t border-gray-100">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">Suitable For</h2>
          
          <div className="space-y-6">
            {suitableFor.map((item, index) => (
              <div key={index} className="pb-6 border-b border-gray-200 last:border-b-0">
                <h3 className="text-lg font-bold text-gray-900 mb-2">{item.condition}</h3>
                <p className="text-gray-600 text-base leading-relaxed">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-12 px-6 border-t border-gray-100">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-2xl font-bold text-gray-900 mb-8">Therapy Benefits</h2>
          
          <div className="space-y-5">
            <div>
              <strong className="text-gray-900 text-base">Dual-Task Training</strong>
              <p className="text-gray-600 text-base mt-1 leading-relaxed">
                Simultaneously addresses cognitive and motor deficits for comprehensive rehabilitation
              </p>
            </div>

            <div>
              <strong className="text-gray-900 text-base">High Engagement</strong>
              <p className="text-gray-600 text-base mt-1 leading-relaxed">
                Colorful, game-based exercises maintain patient motivation throughout therapy
              </p>
            </div>

            <div>
              <strong className="text-gray-900 text-base">Versatile Application</strong>
              <p className="text-gray-600 text-base mt-1 leading-relaxed">
                Suitable for individual therapy, group sessions, and various patient populations
              </p>
            </div>

            <div>
              <strong className="text-gray-900 text-base">Objective Assessment</strong>
              <p className="text-gray-600 text-base mt-1 leading-relaxed">
                Automated performance tracking provides quantifiable data for progress evaluation
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-white py-8 px-6 border-t border-gray-100 mt-8">
        <div className="max-w-7xl mx-auto text-center">
          <p className="text-gray-500 text-sm">
            © 2025 Accelerated Recovery Centre. All rights reserved.
          </p>
          <a 
            href="https://readdy.ai/?ref=logo" 
            target="_blank" 
            rel="nofollow noopener noreferrer"
            className="text-gray-400 text-xs hover:text-gray-600 transition-colors mt-2 inline-block cursor-pointer"
          >
            Powered by Readdy
          </a>
        </div>
      </footer>
    </div>
  );
}
